import PATHS from '../../Routers/PATHS';

/**
 * Primary navigation.
 *
 * The new design ships three links where the old header carried nine. The
 * pages that lost their link — the blog, courses, honour roll and the rest —
 * are deleted.
 */
export const NAV_LINKS = [
    { key: 'home', labelKey: 'v2.nav.home', to: PATHS.LANDING_HOME.URL },
    { key: 'curricula', labelKey: 'v2.nav.curricula', to: PATHS.LANDING_CERTIFICATIONS.URL },
    { key: 'contact', labelKey: 'v2.nav.contact', to: PATHS.LANDING_CONTACTUS.URL },
];

export const FOOTER_LINK_GROUPS = [
    {
        key: 'links',
        titleKey: 'v2.footer.links',
        items: [
            { key: 'curricula', labelKey: 'v2.nav.curricula', to: PATHS.LANDING_CERTIFICATIONS.URL },
            { key: 'contact', labelKey: 'v2.footer.contactUs', to: PATHS.LANDING_CONTACTUS.URL },
        ],
    },
    {
        key: 'legal',
        titleKey: 'v2.footer.legal',
        items: [
            { key: 'privacy', labelKey: 'v2.footer.privacy', to: PATHS.LANDING_PRIVACY_POLICY.URL },
            { key: 'terms', labelKey: 'v2.footer.terms', to: PATHS.LANDING_TERMS_AND_CONDITIONS.URL },
        ],
    },
];

/**
 * Shown in the footer and again on the contact page. Kept in one place so the
 * two cannot drift apart.
 */
export const CONTACT_INFO = {
    email: 'info@learning-go.com',
    phones: [
        { key: 'support', labelKey: 'v2.contact.support', number: '+974 3366-3877' },
        { key: 'sales', labelKey: 'v2.contact.sales', number: '+974 3366-5583' },
    ],
    address: {
        countryCityKey: 'v2.contact.countryCity',
        streetKey: 'v2.contact.street',
        buildingKey: 'v2.contact.building',
    },
    whatsapp: 'https://wa.me/message/P3UYHOZMQMESH1',
};

/**
 * Carried over from the current footer — these accounts are live.
 *
 * The redesigned footer shows four marks, not five: the LinkedIn page is still
 * live but the design drops it, so it is commented out rather than deleted —
 * putting it back is one line if that turns out to be an oversight.
 */
export const SOCIAL_LINKS = [
    { key: 'facebook', href: 'https://www.facebook.com/Learning-Go-101343875302322' },
    { key: 'twitter', href: 'https://twitter.com/Learning_Go_Com' },
    { key: 'instagram', href: 'https://www.instagram.com/Learning_Go_Com/' },
    { key: 'youtube', href: 'https://www.youtube.com/channel/UCve6GRUj0UkqtqbP8vFmexg' },
    // { key: 'linkedin', href: 'https://www.linkedin.com/company/learning-go/' },
];

/**
 * The contact page's two forms, in the order the switch shows them: in RTL the
 * first entry sits on the right, which is where the design puts the message
 * tab — the one the page opens on. The complaint tab follows it on the left.
 *
 * Read off the rendered frame rather than off the layer order, which is the
 * opposite: Figma exports a row left-to-right whatever the language, so the
 * layer listed first is the one drawn on the *left*.
 *
 * Display order is all this decides. The default tab is named on its own below
 * rather than read off index 0, so reordering the switch never quietly changes
 * which form opens or which one the URL leaves unsaid.
 */
export const CONTACT_TABS = [
    { id: 'message', labelKey: 'v2.contact.tabs.message' },
    { id: 'complaint', labelKey: 'v2.contact.tabs.complaint' },
];

/**
 * The form the page opens on, and the one `/contactus` carries no `?tab=` for;
 * the other answers to `/contactus?tab=complaint`, so a link can open it.
 */
export const CONTACT_DEFAULT_TAB = 'message';

/** Attachment rules printed on the contact and complaint forms. */
export const ATTACHMENT_RULES = {
    maxFiles: 5,
    maxBytesPerFile: 10 * 1024 * 1024,
    acceptedMimeTypes: [
        'image/png',
        'image/jpeg',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    /** What the file picker offers, and what the hint under the dropzone says. */
    acceptAttribute: '.png,.jpg,.jpeg,.pdf,.doc,.docx',
    extensionsLabel: 'PNG, JPG, PDF, DOCX',
};

/** Backend keys behind /public/setting/<key>/value. */
export const SETTING_KEYS = {
    privacyPolicy: 'PRIVACY_POLICY',
    termsAndConditions: 'TERMS_CONDITIONS',
};

/**
 * Anchors for the sticky section nav on the certificate detail page, in
 * document order — which is also the order the bar reads them in to decide
 * which section the reader is currently inside.
 *
 * The page drops any section that has nothing to show before handing this to
 * the bar, so a certificate with no questions gets a shorter bar rather than a
 * link that scrolls nowhere.
 */
export const CERT_DETAIL_SECTIONS = [
    { id: 'accreditation', labelKey: 'v2.cert.nav.accreditation' },
    { id: 'packages', labelKey: 'v2.cert.nav.packages' },
    { id: 'about', labelKey: 'v2.cert.nav.about' },
    { id: 'faq', labelKey: 'v2.cert.nav.faq' },
];
